<template>
  <div class="cal-page">
    <!-- Page toolbar -->
    <div class="cal-toolbar">
      <div class="cal-toolbar__left">
        <span class="cal-toolbar__accent" aria-hidden="true"/>
        <div>
          <h1 class="cal-toolbar__title">Calendar</h1>
          <p class="cal-toolbar__subtitle">
            {{ monthLabel }} {{ year }} · {{ today.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' }) }}
          </p>
        </div>
      </div>
      <div class="cal-toolbar__right">
        <button type="button" class="cal-btn cal-btn--ghost" @click="goToToday">Today</button>
        <div class="cal-nav">
          <button type="button" class="cal-nav__btn" aria-label="Previous month" @click="changeMonth(-1)">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
          </button>
          <button type="button" class="cal-nav__btn" aria-label="Next month" @click="changeMonth(1)">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <div class="cal-layout">
      <!-- Main calendar grid -->
      <section class="cal-card cal-card--grid">
        <header class="cal-card__head">
          <h2 class="cal-card__title">{{ monthLabel }} {{ year }}</h2>
          <span class="cal-card__hint">Click a date to see its events</span>
        </header>
        <div class="cal-card__body">
          <div class="cal-weekdays">
            <div v-for="d in weekdayLabels" :key="d" class="cal-weekday">{{ d }}</div>
          </div>
          <div class="cal-grid">
            <button
              v-for="(cell, idx) in cells"
              :key="idx"
              type="button"
              class="cal-cell"
              :class="{
                'cal-cell--outside': !cell.inMonth,
                'cal-cell--today': cell.isToday,
                'cal-cell--weekend': cell.isWeekend,
                'cal-cell--selected': cell.iso === selectedIso,
                'cal-cell--has-holiday': cell.holidays.length > 0,
              }"
              @click="selectDate(cell)"
            >
              <span class="cal-cell__num">{{ cell.day }}</span>
              <span v-if="cell.holidays.length" class="cal-cell__events">
                <span
                  v-for="(h, i) in cell.holidays.slice(0, 2)"
                  :key="i"
                  class="cal-cell__event"
                  :class="`cal-cell__event--${h.type}`"
                  :title="h.name"
                >
                  {{ h.name }}
                </span>
                <span v-if="cell.holidays.length > 2" class="cal-cell__more">+{{ cell.holidays.length - 2 }} more</span>
              </span>
            </button>
          </div>
        </div>
      </section>

      <!-- Side rail: selected day + upcoming holidays + legend -->
      <aside class="cal-side">
        <section class="cal-card">
          <header class="cal-card__head">
            <h2 class="cal-card__title">{{ selectedHumanLabel }}</h2>
          </header>
          <div class="cal-card__body cal-side__day">
            <div v-if="selectedHolidays.length === 0" class="cal-side__empty">
              No events on this day.
            </div>
            <ul v-else class="cal-side__events">
              <li v-for="(h, i) in selectedHolidays" :key="i" class="cal-side__event">
                <span class="cal-side__dot" :class="`cal-side__dot--${h.type}`" aria-hidden="true"/>
                <div class="cal-side__event-body">
                  <div class="cal-side__event-name">{{ h.name }}</div>
                  <div class="cal-side__event-meta">{{ typeLabel(h.type) }}<span v-if="h.regional"> · Regional</span></div>
                </div>
              </li>
            </ul>
          </div>
        </section>

        <section class="cal-card">
          <header class="cal-card__head">
            <h2 class="cal-card__title">Upcoming</h2>
            <span class="cal-card__hint">Next 6 events</span>
          </header>
          <ul class="cal-card__body cal-upcoming">
            <li v-if="upcomingHolidays.length === 0" class="cal-side__empty">No upcoming events.</li>
            <li v-for="(h, i) in upcomingHolidays" :key="i" class="cal-upcoming__item">
              <div class="cal-upcoming__date">
                <div class="cal-upcoming__day">{{ formatDay(h.date) }}</div>
                <div class="cal-upcoming__mon">{{ formatMon(h.date) }}</div>
              </div>
              <div class="cal-upcoming__body">
                <div class="cal-upcoming__name">{{ h.name }}</div>
                <div class="cal-upcoming__meta">{{ formatWeekday(h.date) }} · {{ relativeFromToday(h.date) }}</div>
              </div>
            </li>
          </ul>
        </section>

        <section class="cal-card">
          <header class="cal-card__head">
            <h2 class="cal-card__title">Legend</h2>
          </header>
          <div class="cal-card__body cal-legend">
            <div class="cal-legend__row"><span class="cal-side__dot cal-side__dot--national"/>National holiday</div>
            <div class="cal-legend__row"><span class="cal-side__dot cal-side__dot--religious"/>Religious</div>
            <div class="cal-legend__row"><span class="cal-side__dot cal-side__dot--cultural"/>Cultural</div>
            <div class="cal-legend__row"><span class="cal-side__dot cal-side__dot--observance"/>Observance</div>
            <div v-if="isAdmin" class="cal-legend__row"><span class="cal-side__dot cal-side__dot--birthday"/>Staff birthday</div>
            <p class="cal-legend__note">
              Lunar-calendar dates (Eid, Holi, Diwali, Onam, etc.) are approximate and may shift by a day or two.
            </p>
          </div>
        </section>
      </aside>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { getStoredRoles } from '@/auth/access'
import {
  getHolidaysForDate,
  getHolidaysInRange,
} from './holidays'

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
]
const WEEKDAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']

function toIso(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export default {
  name: 'Calendar',
  data() {
    const now = new Date()
    return {
      today: now,
      year: now.getFullYear(),
      month: now.getMonth(),
      selectedIso: toIso(now),
      weekdayLabels: WEEKDAYS,
      birthdays: [],
    }
  },
  created() {
    this.fetchBirthdays()
  },
  computed: {
    isAdmin() {
      const roles = getStoredRoles()
      return roles.includes('admin') || roles.includes('superadmin')
    },
    monthLabel() {
      return MONTHS[this.month]
    },
    todayIso() {
      return toIso(this.today)
    },
    // Birthdays keyed by 'M-D' (year-agnostic) so the same record fires
    // every year without us having to expand it across the timeline.
    birthdaysByMonthDay() {
      const map = {}
      for (const b of this.birthdays) {
        const key = `${b.month}-${b.day}`
        if (!map[key]) map[key] = []
        map[key].push({ name: `${b.name}'s Birthday`, type: 'birthday' })
      }
      return map
    },
    cells() {
      const firstOfMonth = new Date(this.year, this.month, 1)
      const startWeekday = firstOfMonth.getDay()
      const start = new Date(this.year, this.month, 1 - startWeekday)

      const cells = []
      for (let i = 0; i < 42; i++) {
        const d = new Date(start)
        d.setDate(start.getDate() + i)
        const iso = toIso(d)
        cells.push({
          date: d,
          iso,
          day: d.getDate(),
          inMonth: d.getMonth() === this.month,
          isToday: iso === this.todayIso,
          isWeekend: d.getDay() === 0 || d.getDay() === 6,
          holidays: this.eventsForIso(iso),
        })
      }
      return cells
    },
    selectedHolidays() {
      return this.eventsForIso(this.selectedIso)
    },
    selectedHumanLabel() {
      const [y, m, d] = this.selectedIso.split('-').map(Number)
      const date = new Date(y, m - 1, d)
      return date.toLocaleDateString(undefined, {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
      })
    },
    upcomingHolidays() {
      // Look one year ahead so each annual event appears exactly once.
      const horizonEnd = new Date(this.today)
      horizonEnd.setFullYear(horizonEnd.getFullYear() + 1)
      const events = [...getHolidaysInRange(this.todayIso, toIso(horizonEnd))]

      const todayDate = new Date(this.today.getFullYear(), this.today.getMonth(), this.today.getDate())
      for (const b of this.birthdays) {
        let next = new Date(todayDate.getFullYear(), b.month - 1, b.day)
        if (next < todayDate) {
          next = new Date(todayDate.getFullYear() + 1, b.month - 1, b.day)
        }
        events.push({
          date: toIso(next),
          name: `${b.name}'s Birthday`,
          type: 'birthday',
        })
      }

      events.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))
      return events.slice(0, 6)
    },
  },
  methods: {
    fetchBirthdays() {
      // Staff birthdays are visible to admins only.
      if (!this.isAdmin) {
        this.birthdays = []
        return
      }
      axios
        .get(`${this.$apiAdress}/api/users/birthdays?token=${localStorage.getItem('api_token')}`)
        .then((res) => { this.birthdays = Array.isArray(res.data) ? res.data : [] })
        .catch(() => { this.birthdays = [] })
    },
    eventsForIso(iso) {
      const parts = iso.split('-')
      const key = `${Number(parts[1])}-${Number(parts[2])}`
      const birthdayEvents = this.birthdaysByMonthDay[key] || []
      return [...getHolidaysForDate(iso), ...birthdayEvents]
    },
    changeMonth(delta) {
      let m = this.month + delta
      let y = this.year
      if (m < 0) { m = 11; y -= 1 }
      if (m > 11) { m = 0; y += 1 }
      this.month = m
      this.year = y
    },
    goToToday() {
      const now = new Date()
      this.year = now.getFullYear()
      this.month = now.getMonth()
      this.selectedIso = toIso(now)
    },
    selectDate(cell) {
      this.selectedIso = cell.iso
      if (!cell.inMonth) {
        this.year = cell.date.getFullYear()
        this.month = cell.date.getMonth()
      }
    },
    typeLabel(type) {
      switch (type) {
        case 'national':   return 'National holiday'
        case 'religious':  return 'Religious'
        case 'cultural':   return 'Cultural'
        case 'observance': return 'Observance'
        case 'birthday':   return 'Birthday'
        default:           return 'Event'
      }
    },
    formatDay(iso) {
      return iso.slice(-2)
    },
    formatMon(iso) {
      const m = Number(iso.slice(5, 7)) - 1
      return MONTHS[m].slice(0, 3)
    },
    formatWeekday(iso) {
      const [y, m, d] = iso.split('-').map(Number)
      return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: 'long' })
    },
    relativeFromToday(iso) {
      const [y, m, d] = iso.split('-').map(Number)
      const target = new Date(y, m - 1, d)
      const today = new Date(this.today.getFullYear(), this.today.getMonth(), this.today.getDate())
      const diffMs = target.getTime() - today.getTime()
      const days = Math.round(diffMs / (1000 * 60 * 60 * 24))
      if (days === 0) return 'Today'
      if (days === 1) return 'Tomorrow'
      if (days > 1 && days < 7) return `in ${days} days`
      if (days >= 7 && days < 30) {
        const w = Math.round(days / 7)
        return `in ${w} week${w === 1 ? '' : 's'}`
      }
      const months = Math.round(days / 30)
      return `in ${months} month${months === 1 ? '' : 's'}`
    },
  },
}
</script>

<style scoped>
.cal-page {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-bottom: 1.5rem;
}

/* ── Toolbar ─────────────────────────────────────────────────────── */
.cal-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}
.cal-toolbar__left {
  display: flex;
  align-items: stretch;
  gap: 0.75rem;
}
.cal-toolbar__accent {
  width: 4px;
  border-radius: 2px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
}
.cal-toolbar__title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
}
.cal-toolbar__subtitle {
  margin: 2px 0 0;
  font-size: 0.78rem;
  color: #6b7280;
}
.cal-toolbar__right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.cal-btn {
  height: 36px;
  padding: 0 0.9rem;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 600;
  border: 1px solid #d8dde6;
  background: #ffffff;
  color: #1f2a44;
  cursor: pointer;
  transition: background 0.12s ease, border-color 0.12s ease;
}
.cal-btn--ghost:hover {
  background: #f5fbf8;
  border-color: #16a07a;
  color: #0e5b46;
}

.cal-btn:focus,
.cal-btn:focus-visible { outline: 0; box-shadow: none; }

.cal-nav {
  display: inline-flex;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  overflow: hidden;
  background: #ffffff;
}
.cal-nav__btn {
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 0;
  color: #4b5563;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;
}
.cal-nav__btn + .cal-nav__btn { border-left: 1px solid #e5e9f2; }
.cal-nav__btn:hover { background: #f5fbf8; color: #0e5b46; }
.cal-nav__btn:focus,
.cal-nav__btn:focus-visible { outline: 0; box-shadow: none; }

/* ── Layout ──────────────────────────────────────────────────────── */
.cal-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 1rem;
}
@media (max-width: 991px) {
  .cal-layout { grid-template-columns: 1fr; }
}

/* ── Card chrome (matches design system) ─────────────────────────── */
.cal-card {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.cal-card__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding: 0.7rem 0.95rem;
  background: #fbfcfe;
  border-bottom: 1px solid #eef1f7;
}
.cal-card__title {
  margin: 0;
  font-size: 0.92rem;
  font-weight: 700;
  color: #1f2a44;
}
.cal-card__hint {
  font-size: 0.7rem;
  color: #94a3b8;
  font-weight: 500;
}
.cal-card__body { padding: 0.85rem; }

/* ── Grid header (weekday row) ───────────────────────────────────── */
.cal-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
  margin-bottom: 6px;
}
.cal-weekday {
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
  text-align: center;
  padding: 0.3rem 0;
}

/* ── Calendar grid (cells) ───────────────────────────────────────── */
.cal-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-auto-rows: 96px;
  gap: 6px;
}
.cal-cell {
  padding: 0.5rem;
  background: #ffffff;
  border: 1px solid #eef1f7;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  overflow: hidden;
  transition: background 0.12s ease, border-color 0.12s ease, transform 0.05s ease;
}
.cal-cell:hover {
  background: #f5fbf8;
  border-color: #d1ece1;
}

.cal-cell:focus,
.cal-cell:focus-visible { outline: 0; box-shadow: none; }
.cal-cell--today:focus,
.cal-cell--today:focus-visible { box-shadow: inset 0 0 0 1px #16a07a; }
.cal-cell--weekend { background: #fafbfd; }
.cal-cell--outside .cal-cell__num { color: #cbd5e1; }
.cal-cell--outside { background: #fbfbfd; }
.cal-cell--today {
  border-color: #16a07a;
  box-shadow: inset 0 0 0 1px #16a07a;
}
.cal-cell--today .cal-cell__num {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
}
.cal-cell--selected {
  border-color: #16a07a;
  background: #eaf7f1;
}
.cal-cell__num {
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 0.82rem;
  font-weight: 600;
  color: #1f2a44;
}
.cal-cell__events {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
}
.cal-cell__event {
  font-size: 0.66rem;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.cal-cell__event--national   { background: #fee2e2; color: #b91c1c; }
.cal-cell__event--religious  { background: #ede9fe; color: #6d28d9; }
.cal-cell__event--cultural   { background: #fef3c7; color: #b45309; }
.cal-cell__event--observance { background: #e0f2fe; color: #075985; }
.cal-cell__event--birthday   { background: #fce7f3; color: #be185d; }
.cal-cell__more {
  font-size: 0.62rem;
  color: #6b7280;
  font-weight: 600;
}

/* ── Side rail ───────────────────────────────────────────────────── */
.cal-side {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.cal-side__empty {
  font-size: 0.78rem;
  color: #94a3b8;
  font-style: italic;
}
.cal-side__events {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}
.cal-side__event {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
}
.cal-side__dot {
  flex: 0 0 auto;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  margin-top: 6px;
}
.cal-side__dot--national   { background: #ef4444; }
.cal-side__dot--religious  { background: #8b5cf6; }
.cal-side__dot--cultural   { background: #f59e0b; }
.cal-side__dot--observance { background: #0ea5e9; }
.cal-side__dot--birthday   { background: #ec4899; }
.cal-side__event-name {
  font-size: 0.84rem;
  font-weight: 600;
  color: #1f2a44;
}
.cal-side__event-meta {
  font-size: 0.7rem;
  color: #6b7280;
  margin-top: 2px;
}

/* Upcoming list */
.cal-upcoming {
  list-style: none;
  margin: 0;
  display: flex;
  flex-direction: column;
}
.cal-upcoming__item {
  display: flex;
  gap: 0.65rem;
  align-items: center;
  padding: 0.55rem 0;
  border-bottom: 1px solid #f1f3f8;
}
.cal-upcoming__item:last-child { border-bottom: 0; }
.cal-upcoming__date {
  flex: 0 0 auto;
  width: 42px;
  text-align: center;
  border-radius: 8px;
  padding: 4px 0;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  line-height: 1.1;
}
.cal-upcoming__day {
  font-size: 0.95rem;
  font-weight: 700;
}
.cal-upcoming__mon {
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  opacity: 0.85;
}
.cal-upcoming__name {
  font-size: 0.82rem;
  font-weight: 600;
  color: #1f2a44;
}
.cal-upcoming__meta {
  font-size: 0.68rem;
  color: #6b7280;
  margin-top: 2px;
}

/* Legend */
.cal-legend {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}
.cal-legend__row {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  font-size: 0.78rem;
  color: #4b5563;
}
.cal-legend__note {
  margin: 0.5rem 0 0;
  font-size: 0.7rem;
  color: #94a3b8;
  font-style: italic;
  line-height: 1.4;
}
</style>
